import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTimer } from 'hooks/useTimer';

import { sendSmsCode } from 'data-providers/mainAnketaProvider';
import {
  INITIAL_DURATION,
  PROCESS_INFO_DESCRIPTION,
  PROCESS_INFO_STATES,
  PROCESS_INFO_TITLE,
} from '../constants';

import { Spinner } from 'components/uikit/Spinner/Spinner';
import { AlertTriangleMIcon } from 'assets/icons';

import * as SMSVerificationStore from 'store/confirmSMS';
import * as help from '../helpers';

export const useMainSMSVerificationPresenter = ({ appId, dealerId, customer, onClick }) => {
  const rootDispatch = useDispatch();

  const {
    timerStartTime,
    isViewResendButton,
    isViewTryoutButton,
    isViewTimeoutButton,
    awaitingState,
    wrongCodeState,
    globalTimeoutState,
    totalResendTries,
    currentResendTries,
  } = useSelector((store) => store.confirmSMS.teleponyConfirm);
  const { id: userId } = useSelector((store) => store.user);

  const timer = useTimer(INITIAL_DURATION);
  const hasNoSMS = currentResendTries >= totalResendTries;

  const statusMap = [
    {
      id: 1,
      condition: awaitingState,
      props: {
        title: PROCESS_INFO_TITLE.awaiting,
        description: PROCESS_INFO_DESCRIPTION.awaiting,
        Icon: <Spinner />,
        status: PROCESS_INFO_STATES.awaiting,
      },
    },
    {
      id: 2,
      condition: wrongCodeState,
      props: {
        title: PROCESS_INFO_TITLE.wrongCode,
        description: PROCESS_INFO_DESCRIPTION.wrongCode,
        Icon: <AlertTriangleMIcon />,
        status: PROCESS_INFO_STATES.wrongCode,
      },
    },
    {
      id: 3,
      condition: globalTimeoutState,
      props: {
        title: PROCESS_INFO_TITLE.globalTimeout,
        description: PROCESS_INFO_DESCRIPTION.globalTimeout,
        Icon: <AlertTriangleMIcon />,
        status: PROCESS_INFO_STATES.globalTimeout,
      },
    },
  ];

  const resendCodeHandler = async (e) => {
    e.preventDefault();
    rootDispatch(SMSVerificationStore.setFetchError(false));
    rootDispatch(SMSVerificationStore.setTeleponyAwatingState(true));
    rootDispatch(SMSVerificationStore.setTeleponyWrongCodeState(false));

    if (!appId || !dealerId || !help.checkCustomer(customer)) {
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    rootDispatch(SMSVerificationStore.setTeleponyViewResendButton(false));
    rootDispatch(SMSVerificationStore.setTeleponyTimerStart(now));

    timer.start(now);

    const otpResult = await sendSmsCode({
      appId,
      dealerId,
      data: help.getSmsParams(customer),
      userId,
    });

    if (!otpResult) {
      rootDispatch(SMSVerificationStore.setFetchError(true));
      return;
    }

    rootDispatch(SMSVerificationStore.setTeleponyTotalResendTries(otpResult.total));
    rootDispatch(SMSVerificationStore.setTeleponyCurrentResendTries(otpResult.current_count));
    rootDispatch(SMSVerificationStore.setOtpId(otpResult?.id || null));
  };

  const noSMSClickHandler = useCallback((e) => {
    resendCodeHandler(e);
    onClick();
  }, []);

  const getSubTitle = useMemo(() => {
    if (!isViewResendButton) {
      return null;
    }

    if (currentResendTries === 1) {
      return `Осталось 2 попытки`;
    }

    if (currentResendTries === 2) {
      return `Осталась 1 попытка`;
    }

    if (currentResendTries >= 3) {
      return 'Попыток не осталось';
    }

    return null;
  }, [isViewResendButton, currentResendTries, totalResendTries]);

  useEffect(() => {
    if (timerStartTime && !isViewResendButton) {
      timer.start(timerStartTime);
    }

    return () => timer.stop();
  }, [timerStartTime]);

  useEffect(() => {
    if (timer.seconds <= 0 && !isViewResendButton) {
      rootDispatch(SMSVerificationStore.setTeleponyViewResendButton(true));
    }
  }, [timer.seconds, isViewResendButton]);

  useEffect(() => {
    let timeout;
    if (hasNoSMS && isViewResendButton) {
      timeout = setTimeout(() => {
        rootDispatch(SMSVerificationStore.setTeleponyViewTryoutButton(true));
      }, 10000);
    }

    return () => clearTimeout(timeout);
  }, [hasNoSMS, isViewResendButton]);

  return {
    statusMap,
    timer,
    isViewResendButton,
    isViewTryoutButton,
    isViewTimeoutButton,
    hasNoSMS,
    getSubTitle,
    resendCodeHandler,
    noSMSClickHandler,
  };
};
